import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import WISHeroSection from './components/WISHeroSection';
import WarehouseChallengesSection from './components/WarehouseChallengesSection';
import WISFeatureModulesSection from './components/WISFeatureModulesSection';
import WISIntegrationSection from './components/WISIntegrationSection';
import WISTestimonialsSection from './components/WISTestimonialsSection';
import WISContactSection from './components/WISContactSection';

const WISPage = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const handleDemoRequest = () => {
    const demoSection = document.getElementById('wis-demo-request');
    if (demoSection) {
      const headerHeight = 80;
      const targetPosition = demoSection.offsetTop - headerHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleFeaturesExploration = () => {
    const featuresSection = document.getElementById('wis-features');
    if (featuresSection) {
      const headerHeight = 80;
      const targetPosition = featuresSection.offsetTop - headerHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main>
        <WISHeroSection 
          onDemoRequest={handleDemoRequest}
          onFeaturesExploration={handleFeaturesExploration}
        />
        
        <WarehouseChallengesSection />
        
        <WISFeatureModulesSection />
        
        <WISIntegrationSection />
        
        <WISTestimonialsSection />
        
        <WISContactSection />
      </main>

      {/* Footer */}
      <footer className="bg-primary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center w-10 h-10 bg-white rounded-lg">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="var(--color-primary)"/>
                    <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold">WIS - Warehouse Information System</h3>
                  <p className="text-sm opacity-80">
                    {currentLanguage === 'en' ? 'Smart Warehouse Management for Aluminum Manufacturers' : 'एल्यूमीनियम निर्माताओं के लिए स्मार्ट वेयरहाउस प्रबंधन'}
                  </p>
                </div>
              </div>
              <p className="text-sm opacity-80 leading-relaxed mb-6">
                {currentLanguage === 'en' ?'Achieve 99.9% inventory accuracy and real-time material tracking across multiple locations with our specialized warehouse management solution for aluminum extrusion manufacturers.' :'एल्यूमीनियम एक्सट्रूज़न निर्माताओं के लिए हमारे विशेष वेयरहाउस प्रबंधन समाधान के साथ 99.9% इन्वेंटरी सटीकता और कई स्थानों पर रियल-टाइम मैटेरियल ट्रैकिंग प्राप्त करें।'
                }
              </p>
              <div className="flex space-x-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">99.9%</div>
                  <div className="text-xs opacity-80">
                    {currentLanguage === 'en' ? 'Inventory Accuracy' : 'इन्वेंटरी सटीकता'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">50+</div>
                  <div className="text-xs opacity-80">
                    {currentLanguage === 'en' ? 'Warehouses' : 'वेयरहाउस'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">24/7</div>
                  <div className="text-xs opacity-80">
                    {currentLanguage === 'en' ? 'Monitoring' : 'निगरानी'}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">
                {currentLanguage === 'en' ? 'WIS Features' : 'WIS सुविधाएं'}
              </h4>
              <ul className="space-y-2 text-sm opacity-80">
                <li><a href="#wis-features" className="hover:opacity-100 transition-opacity">
                  {currentLanguage === 'en' ? 'Inventory Management' : 'इन्वेंटरी प्रबंधन'}
                </a></li>
                <li><a href="#wis-features" className="hover:opacity-100 transition-opacity">
                  {currentLanguage === 'en' ? 'Material Tracking' : 'मैटेरियल ट्रैकिंग'}
                </a></li>
                <li><a href="#wis-features" className="hover:opacity-100 transition-opacity">
                  {currentLanguage === 'en' ? 'Layout Optimization' : 'लेआउट अनुकूलन'}
                </a></li>
                <li><a href="#wis-features" className="hover:opacity-100 transition-opacity">
                  {currentLanguage === 'en' ? 'Automated Reordering' : 'स्वचालित पुनर्व्यवस्था'}
                </a></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold mb-4">
                {currentLanguage === 'en' ? 'Contact WIS Team' : 'WIS टीम से संपर्क करें'}
              </h4>
              <div className="space-y-2 text-sm opacity-80">
                <p>
                  {currentLanguage === 'en' ? 'Email: wis@alumerp.com' : 'ईमेल: wis@alumerp.com'}
                </p>
                <p>
                  {currentLanguage === 'en' ? 'Phone: +91 9876543210' : 'फोन: +91 9876543210'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WISPage;